import { useCallback, useState } from 'react';

type History<T> = { past: T[]; present: T; future: T[] };

/**
 * Snapshot undo/redo, for the image editor.
 *
 * Snapshots rather than an operation log: every mutation commits the whole
 * next state, and undo is nothing but popping the previous one. The states
 * share their untouched arrays structurally (each commit spreads, it does not
 * clone), so a hundred strokes of history hold one copy of the strokes that
 * did not change.
 *
 * `reset` is a commit of the initial state, not a wipe of the stack - which is
 * what makes reset itself undoable, and an accidental reset a one-step
 * recovery instead of a lost drawing.
 */
export const useEditorHistory = <T>(initial: T) => {
  const [history, setHistory] = useState<History<T>>({
    past: [],
    present: initial,
    future: [],
  });

  const commit = useCallback((next: T) => {
    setHistory((h) => ({
      past: [...h.past, h.present],
      present: next,
      future: [],
    }));
  }, []);

  const undo = useCallback(() => {
    setHistory((h) => {
      if (h.past.length === 0) return h;
      return {
        past: h.past.slice(0, -1),
        present: h.past[h.past.length - 1],
        future: [h.present, ...h.future],
      };
    });
  }, []);

  const redo = useCallback(() => {
    setHistory((h) => {
      if (h.future.length === 0) return h;
      return {
        past: [...h.past, h.present],
        present: h.future[0],
        future: h.future.slice(1),
      };
    });
  }, []);

  const reset = useCallback(() => {
    setHistory((h) =>
      // Same reference means nothing was ever committed (or the last commit
      // was this very reset) - a second no-op step would only pollute undo.
      h.present === initial
        ? h
        : { past: [...h.past, h.present], present: initial, future: [] }
    );
  }, [initial]);

  return {
    state: history.present,
    commit,
    undo,
    redo,
    reset,
    canUndo: history.past.length > 0,
    canRedo: history.future.length > 0,
  };
};
